<template>
  <div>
    <p class="text-xl font-bold mb-4">{{ t("title") }}</p>
    <div class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-gray-100">
            <th class="px-4 py-3 text-left text-gray-400 font-semibold text-xs uppercase tracking-wider w-10"></th>
            <th class="px-4 py-3 text-left text-gray-400 font-semibold text-xs uppercase tracking-wider">
              {{ t("store_name") }}
            </th>
            <th class="px-4 py-3 text-right text-gray-400 font-semibold text-xs uppercase tracking-wider">
              {{ t(`challenge.counting.${countingMethod}.column`) }}
            </th>
            <th
              v-if="isGrossSales"
              class="px-4 py-3 text-right text-gray-400 font-semibold text-xs uppercase tracking-wider"
            >
              {{ t("compliance") }}
            </th>
            <th class="px-4 py-3 text-right text-gray-400 font-semibold text-xs uppercase tracking-wider">
              {{ t("total_reward") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="store in displayedStores"
            :key="store.storeId"
            class="border-b border-gray-50"
            :class="expanded && isUserStore(store.storeId) ? 'bg-purple-50' : ''"
          >
            <td class="px-4 py-2.5 text-gray-400 font-medium">{{ store.rank }}</td>
            <td class="px-4 py-2.5 font-medium text-gray-900">
              {{ store.salesChannel }}
              <span
                v-if="store.networkRank && store.networkRank !== store.rank"
                class="text-xs text-gray-400 font-normal"
              >
                (#{{ store.networkRank }} {{ t("network_rank") }})
              </span>
            </td>
            <td class="px-4 py-2.5 text-right font-semibold text-gray-900">{{ store.salesCount }}</td>
            <td
              v-if="isGrossSales"
              class="px-4 py-2.5 text-right font-semibold"
              :class="store.complianceRate >= complianceThreshold ? 'text-green-700' : 'text-red-600'"
            >
              {{ store.complianceRate }} %
            </td>
            <td class="px-4 py-2.5 text-right font-semibold text-gray-900">{{ formatPrice(store.totalReward) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <button
      v-if="hasMore"
      class="mt-4 w-full py-2 text-sm text-primary-dark font-medium bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
      @click="expanded = !expanded"
    >
      {{ expanded ? t("show_less") : t("show_more") }}
    </button>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { formatPrice, computeCompetitionRanking } from "@estaly/ui";
import { COMPLIANCE_THRESHOLD, complianceRate, isGrossSalesCounting } from "@/utils/challengeTiers";

const INITIAL_COUNT = 5;

export default {
  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    stores: {
      type: Array,
      required: true,
    },
    userStoreIds: {
      type: Array,
      default: () => [],
    },
    countingMethod: {
      type: String,
      default: "plan_sales",
    },
  },

  data() {
    return {
      expanded: false,
    };
  },

  computed: {
    isGrossSales() {
      return isGrossSalesCounting(this.countingMethod);
    },

    complianceThreshold() {
      return COMPLIANCE_THRESHOLD;
    },

    allStoresRanked() {
      const withCompliance = this.stores.map((store) => ({
        ...store,
        complianceRate: complianceRate(store.leadsCount, store.salesCount),
      }));
      const sorted = [...withCompliance].sort((a, b) => b.salesCount - a.salesCount);
      return computeCompetitionRanking(sorted, "salesCount");
    },

    userStoresRanked() {
      if (!this.userStoreIds.length) return this.allStoresRanked;
      return this.allStoresRanked.filter((s) => this.userStoreIds.includes(s.storeId));
    },

    displayedStores() {
      return this.expanded ? this.allStoresRanked : this.userStoresRanked.slice(0, INITIAL_COUNT);
    },

    hasMore() {
      return (
        this.expanded ||
        this.userStoresRanked.length > INITIAL_COUNT ||
        this.stores.length > this.userStoresRanked.length
      );
    },
  },

  methods: {
    formatPrice,

    isUserStore(storeId) {
      return this.userStoreIds.includes(storeId);
    },
  },
};
</script>

<i18n src="./TieredChallengePilotageStoreRanking.i18n.json"></i18n>
